import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, CheckCircle, Users, Building2, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { notifyFormSubmission } from '@/lib/notifyForm';
import { useToast } from '@/hooks/use-toast';

interface TrainingBookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  trainingLevel: string;
  trainingTitle: string;
}

const TrainingBookingForm = ({ isOpen, onClose, trainingLevel, trainingTitle }: TrainingBookingFormProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingType, setBookingType] = useState<'individual' | 'group' | 'organization'>('individual');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    participantsCount: 1,
    preferredDates: '',
    specialRequirements: ''
  });

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('training_bookings')
        .insert({
          training_level: trainingLevel,
          training_title: trainingTitle,
          booking_type: bookingType,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          job_title: formData.jobTitle || null,
          participants_count: bookingType === 'individual' ? 1 : formData.participantsCount,
          preferred_dates: formData.preferredDates || null,
          special_requirements: formData.specialRequirements || null
        });

      if (error) throw error;

      notifyFormSubmission('Training Booking', {
        training_level: trainingLevel,
        training_title: trainingTitle,
        booking_type: bookingType,
        ...formData,
      });

      setStep(3);
      toast({
        title: "Booking Request Submitted",
        description: "Our expert team will reach out to you soon!",
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast({
        title: "Error",
        description: "Failed to submit booking request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setBookingType('individual');
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      jobTitle: '',
      participantsCount: 1,
      preferredDates: '',
      specialRequirements: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Book Training: {trainingTitle}
            <Button variant="ghost" size="lg" onClick={handleClose} aria-label="Close dialog" className="h-11 w-11">
              <X className="h-5 w-5" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Fill out the form below and our team will contact you with details.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Select Booking Type
              </CardTitle>
              <CardDescription>
                Choose whether you're booking for yourself or your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup 
                value={bookingType} 
                onValueChange={(value) => setBookingType(value as typeof bookingType)}
                className="space-y-4"
              >
                <div className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-accent">
                  <RadioGroupItem value="individual" id="individual" />
                  <Label htmlFor="individual" className="flex items-center space-x-3 cursor-pointer flex-1">
                    <User className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">Individual Training</div>
                      <div className="text-sm text-muted-foreground">Personal skill development</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-accent">
                  <RadioGroupItem value="group" id="group" />
                  <Label htmlFor="group" className="flex items-center space-x-3 cursor-pointer flex-1">
                    <Users className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">Group Training</div>
                      <div className="text-sm text-muted-foreground">Training for small teams (2-10 people)</div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-accent">
                  <RadioGroupItem value="organization" id="organization" />
                  <Label htmlFor="organization" className="flex items-center space-x-3 cursor-pointer flex-1">
                    <Building2 className="h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">Organization Training</div>
                      <div className="text-sm text-muted-foreground">Enterprise-wide training programs</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
              <Button onClick={() => setStep(2)} className="w-full mt-6">
                Continue
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Tell us about yourself and your training needs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="jobTitle">Job Title</Label>
                  <Input
                    id="jobTitle"
                    value={formData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                  />
                </div>
              </div>

              {(bookingType === 'group' || bookingType === 'organization') && (
                <>
                  <div>
                    <Label htmlFor="company">Company/Organization *</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="participants">Number of Participants</Label>
                    <Input
                      id="participants"
                      type="number"
                      min="1"
                      value={formData.participantsCount}
                      onChange={(e) => handleInputChange('participantsCount', parseInt(e.target.value) || 1)}
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="dates">Preferred Training Dates</Label>
                <Input
                  id="dates"
                  placeholder="e.g., January 2024, Weekends preferred"
                  value={formData.preferredDates}
                  onChange={(e) => handleInputChange('preferredDates', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="requirements">Special Requirements</Label>
                <Textarea
                  id="requirements"
                  placeholder="Any specific needs, customizations, or questions..."
                  value={formData.specialRequirements}
                  onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={!formData.name || !formData.email || isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <CardContent className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Request Submitted Successfully!</h3>
              <p className="text-muted-foreground mb-6">
                Thank you for your interest in our {trainingTitle} program. 
                Our expert team will reach out to you within 24 hours to discuss your training needs and provide a customized proposal.
              </p>
              <Button onClick={handleClose}>
                Close
              </Button>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrainingBookingForm;